import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocale } from '@/i18n/useLocale';
import { useSession } from '@/stores/session';
import { paths } from '@/app/paths';
import { Dialog, ErrorState } from '@/ui';
import { nextReminderAt, useRealityCheck } from './realityStore';
import e from '../errors/errors.module.css';

/** Every 60 min of a signed-in session: "You have been playing for N min" → Keep playing / Take a break. */
export function RealityCheck({ tickMs = 20_000 }: { tickMs?: number }) {
  const userId = useSession((st) => (st.status === 'user' ? (st.me?.id ?? null) : null));
  const enabled = useRealityCheck((st) => st.enabled);
  const session = useRealityCheck((st) => st.session);
  const bindUser = useRealityCheck((st) => st.bindUser);
  const acknowledge = useRealityCheck((st) => st.acknowledge);
  const [now, setNow] = useState(() => Date.now());
  const navigate = useNavigate();
  const l = useLocale();
  const intl = useIntl();

  useEffect(() => {
    bindUser(userId);
    setNow(Date.now());
  }, [userId, bindUser]);

  useEffect(() => {
    if (!userId || !enabled) return;
    const t = setInterval(() => setNow(Date.now()), tickMs);
    return () => clearInterval(t);
  }, [userId, enabled, tickMs]);

  const live = session && session.user === userId ? session : null;
  const open = !!live && enabled && now >= nextReminderAt(live.start, live.ack);
  const minutes = live ? Math.floor((now - live.start) / 60_000) : 0;
  const ack = () => acknowledge(now);

  return (
    <Dialog open={open} onClose={ack} width={440} label={intl.formatMessage({ id: 'responsible.reality.title' })}>
      <ErrorState
        className={e.inDialog}
        glyph="◷"
        tone="teal"
        title={<FormattedMessage id="responsible.reality.title" />}
        text={<FormattedMessage id="responsible.reality.text" values={{ minutes }} />}
        primary={{ label: <FormattedMessage id="responsible.reality.keep" />, onClick: ack }}
        secondary={{
          label: <FormattedMessage id="responsible.reality.limits" />,
          onClick: () => {
            ack();
            navigate(paths.responsible(l));
          },
        }}
      />
    </Dialog>
  );
}
